// A NAPLÓBEJEGYZÉS DÁTUMÁNAK érvényességi tartománya — egy helyen.
//
// Miért van ez a fájl (2026-09-25, ClickUp 123qmzqz0q4): a napi
// invariáns-ellenőrzés talált egy éles bejegyzést, aminek a dátuma egy
// évnél is távolabb van a jövőben — jellemzően elgépelt évszám. A
// bejegyzés maga ártalmatlan (az `isFutureEntry()` mindenhol kiszűri a
// számításokból), a napi riasztást viszont pirosra állítja, és a
// bevitel-oldalon SEMMI nem akadályozta meg.
//
// Két rés volt egyszerre:
//   1. A Napló dátum-mezőin nem volt `min`/`max` — az Onboarding
//      mezőin MÁR VOLT (`max={ma}`), csak nem terjedt ki a Naplóra.
//   2. A `validateEntryDate` (korábban a `Naplo.tsx`-ben, lokálisan)
//      kizárólag az ÜRES dátumot utasította el — bármilyen évszámot
//      elfogadott. Ez a 2026-08-20-i lelet (ClickUp #86cb81b2t)
//      minimális javítása volt: a natív <input type="date"> üresre
//      törölhető, és az üres string csendben bekerült Firestore-ba.
//
// A CLAUDE.md 10. szabálya szerint a felhasználónak megjelenített és a
// számításokban használt dátum közös, tesztelt függvényből jön — ezért
// került ki a komponensből ide, unit tesztekkel.
//
// ⚠️ A kliensoldali ellenőrzés KÉNYELEM, NEM GARANCIA. A tényleges
// garancia egy `validDate()` lenne a `firestore.rules`-ban, a
// `validWeight`/`validGlucose` mintájára — az `entries.date` ott ma is
// ellenőrizetlen (2026-09-14-i biztonsági kör, ClickUp #123qmzqyapx).
// Termékdöntés 2026-09-25: az a réteg a natív verdikt utáni
// rules-körben megy, mert külön deployt és külön jóváhagyást kíván.

import { localDateString } from "./entryDate";

/**
 * Meddig előre engedjük a bejegyzés dátumát.
 *
 * ⚠️ Miért pont 30 nap, és miért NEM „ma": a Napló szándékosan engedi a
 * közeli előre-naplózást (tervezett injekció). A 30 napos határ
 * SZÁNDÉKOSAN egyezik azzal a sávval, amit a napi invariáns-ellenőrzés
 * „magyarázható előre-tervezésnek" tekint (`scripts/invariants/`) — így
 * a kliens korlátja és a monitoring küszöbe ugyanazt a határt mondja,
 * és nem keletkezhet olyan bejegyzés, amit a saját monitoringunk
 * gyanúsnak jelöl.
 *
 * Az Onboarding ennél SZIGORÚBB (`max={ma}`), és az úgy is marad: a
 * terápia kezdete és a visszamenőleges előzmény definíció szerint múlt.
 */
export const MAX_FUTURE_DAYS = 30;

/**
 * A legkorábbi elfogadott dátum.
 *
 * Bőkezűen van megválasztva: a bejegyzések ~75%-a visszamenőleges, és
 * nem akarunk jogos backfillt blokkolni. A célja kizárólag a nyilvánvaló
 * évszám-elgépelés kiszűrése (1025, 1925, 2O25 → NaN). Ha valaha
 * korábbi adat kell, az termékdöntés, nem itt orvosolandó.
 */
export const MIN_ENTRY_DATE = "2015-01-01";

/** Pontosan "YYYY-MM-DD" — a natív <input type="date"> alakja. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A dátum-mező `min`/`max` attribútumai.
 *
 * A natív dátumválasztó (iOS-en is) tiszteletben tartja, tehát a hibás
 * év már be sem vihető — ez az első védelmi réteg. Önmagában NEM elég:
 * a Napló űrlapjain `noValidate` van, tehát a beküldést nem blokkolja.
 * A tényleges kliensoldali kapu a `validateEntryDate()` a mentésben.
 */
export function entryDateBounds(now: Date = new Date()): { min: string; max: string } {
  const max = new Date(now);
  max.setDate(max.getDate() + MAX_FUTURE_DAYS);
  return { min: MIN_ENTRY_DATE, max: localDateString(max) };
}

/**
 * Menthető-e ez a dátum egy naplóbejegyzésben. Magyar hibaüzenetet ad
 * vissza, vagy `null`-t, ha érvényes — ugyanaz a szerződés, mint a
 * `validateWeight` / `validateGlucose`.
 *
 * A HATÁROK BENNE VANNAK a tartományban (a `max` napra még lehet menteni).
 *
 * A string-összehasonlítás szándékos, és ugyanaz az elv, mint az
 * `isFutureEntry()`-nél: a `date` egy puszta "YYYY-MM-DD" naptári nap, a
 * lexikografikus rendezés megegyezik az időrendivel, és így elkerüljük
 * azt a hibaosztályt, ahol egy bare dátum-string UTC éjfélként
 * értelmeződik (éjfél körül más napra esne, mint amit a user lát).
 */
export function validateEntryDate(d: string, now: Date = new Date()): string | null {
  // 2026-08-20 éles lelet (ClickUp #86cb81b2t): a natív <input type="date">
  // üresre törölhető a böngészőben, és mentéskor az üres string csendben
  // bekerült Firestore-ba (a súly-/vércukor-validáció megvolt, a dátumé nem).
  if (!d) return "Add meg a dátumot.";

  if (!ISO_DAY.test(d)) return "Érvénytelen dátum.";

  // Naptárilag létező nap-e (pl. "2026-02-31" alakilag jó, de nem létezik).
  const [y, m, day] = d.split("-").map(Number);
  const parsed = new Date(y, m - 1, day);
  if (
    parsed.getFullYear() !== y ||
    parsed.getMonth() !== m - 1 ||
    parsed.getDate() !== day
  ) {
    return "Érvénytelen dátum.";
  }

  const { min, max } = entryDateBounds(now);

  if (d > max) {
    return `A dátum legfeljebb ${MAX_FUTURE_DAYS} nappal lehet a jövőben. Elírtad az évszámot?`;
  }
  if (d < min) {
    return "A dátum nem lehet korábbi, mint 2015. január 1. Elírtad az évszámot?";
  }

  return null;
}
